import { FC } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button } from '@mui/material';
import { Stack } from '@mui/system';

import WaveUINoDataOverlay from '@/shared/components/utilities/WaveUINoDataOverlay';
import MetadataTypeDefinition from '@/shared/types/metadata/metadataTypeDefinition.type';

import WaveUIAccordion from '../../WaveUIAccordion';
import Form from './components/Form';
import CreateMetadataFormFields from './types/createMetadataFormFields.type';
import Module from './types/module.type';

type CreateMetadataFormProps = {
  module: Module;
  metadataTypes: MetadataTypeDefinition;
  forms: (CreateMetadataFormFields & { id: string; panelOpen: boolean })[];
  formErrors: Record<string, Record<string, string>>;
  onDeleteForm: (id: string) => void;
  onAddNewForm: () => void;
  onUpdateFormValue: (id: string, value: Record<string, unknown>) => void;
  onToggleAccordion: (id: string) => void;
};

const CreateMetadataForm: FC<CreateMetadataFormProps> = ({
  module,
  metadataTypes,
  forms,
  formErrors,
  onDeleteForm,
  onAddNewForm,
  onUpdateFormValue,
  onToggleAccordion,
}) => {
  return (
    <div>
      <Stack spacing={0} pt={2} mb={2}>
        {forms.length === 0 && (
          <Stack alignItems='center'>
            <Button
              onClick={onAddNewForm}
              color='inherit'
              variant='contained'
              size='large'
              sx={{
                bgcolor: 'background.paper',
                px: 10,
              }}
            >
              <Stack alignItems='center' spacing={1}>
                <WaveUINoDataOverlay title='Add Metadata' size='small' />
                <AddIcon sx={{ width: 24, height: 24 }} />
              </Stack>
            </Button>
          </Stack>
        )}

        {forms.map((form, index) => {
          return (
            <WaveUIAccordion
              key={form.id}
              title={form.metadata_key || 'New Metadata'}
              elevation={0}
              expanded={form.panelOpen}
              onChange={() => onToggleAccordion(form.id)}
              actions={[
                {
                  label: '',
                  icon: <DeleteIcon sx={{ width: 16, height: 16 }} />,
                  onClick: () => onDeleteForm(form.id),
                },
              ]}
              sx={{
                mx: -2,
                borderTop: index > 0 ? '1px solid' : 'none',
                borderColor: 'divider',
                borderRadius: 0,
              }}
            >
              <Form
                module={module}
                formValues={form}
                formErrors={formErrors[form.id]}
                metadataTypes={metadataTypes}
                getFormValue={(value) => onUpdateFormValue(form.id, value)}
              />
            </WaveUIAccordion>
          );
        })}
      </Stack>

      {forms.length > 0 && (
        <Stack
          sx={{
            alignItems: 'flex-end',
            position: 'sticky',
            bottom: -24,
            mx: -2,
            py: 1,
            px: 2,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <Button
            onClick={onAddNewForm}
            variant='contained'
            color='inherit'
            sx={{
              'width': '100%',
              'margin': 0,
              'boxShadow': 'none',
              '&:hover': {
                boxShadow: 'none',
              },
            }}
          >
            <AddIcon sx={{ width: 24, height: 24 }} />
          </Button>
        </Stack>
      )}
    </div>
  );
};

export default CreateMetadataForm;
